import React, { useState } from 'react';
import { faithService } from '../services/api';
import { FAITH_TRADITION_OPTIONS, FAITH_TRADITIONS } from '../constants/faithTraditions';

const FaithSettings = ({ user, onSettingsChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    faithTrackEnabled: user?.faithPreferences?.faithTrackEnabled || false,
    faithTradition: user?.faithPreferences?.faithTradition || FAITH_TRADITIONS.CHRISTIAN
  });

  const selectedTraditionLabel =
    FAITH_TRADITION_OPTIONS.find((option) => option.value === settings.faithTradition)?.label || settings.faithTradition;

  const handleToggleFaithTrack = async () => {
    const newState = !settings.faithTrackEnabled;
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const startDate = newState ? new Date() : null;
      const updatedSettings = {
        faithTrackEnabled: newState,
        faithTrackStartDate: startDate,
        faithTradition: settings.faithTradition
      };

      await faithService.updateFaithPreferences(user._id, updatedSettings);

      setSettings({
        ...settings,
        faithTrackEnabled: newState
      });

      setMessage({
        type: 'success',
        text: newState 
          ? 'Faith track enabled! A fourth pillar will be added to your dashboard.' 
          : 'Faith track disabled.'
      });

      onSettingsChange && onSettingsChange(updatedSettings);
    } catch (error) {
      console.error('Error updating faith preferences:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update faith track settings'
      });
      // Revert the toggle
      setSettings({ ...settings, faithTrackEnabled: !newState });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTraditionChange = async (e) => {
    const newTradition = e.target.value;
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const updatedSettings = {
        faithTrackEnabled: settings.faithTrackEnabled,
        faithTradition: newTradition
      };

      await faithService.updateFaithPreferences(user._id, updatedSettings);

      setSettings({
        ...settings,
        faithTradition: newTradition
      });

      setMessage({
        type: 'success',
        text: `Faith tradition updated to ${newTradition}`
      });

      onSettingsChange && onSettingsChange(updatedSettings);
    } catch (error) {
      console.error('Error updating faith tradition:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update faith tradition'
      });
      // Revert the change
      setSettings({
        ...settings,
        faithTradition: user?.faithPreferences?.faithTradition || FAITH_TRADITIONS.CHRISTIAN
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="faith-settings border rounded-lg p-6 bg-white shadow-sm">
      <h3 className="text-lg font-semibold mb-6 text-neutral-800">
        Faith Track Settings
      </h3>

      {/* Enable/Disable Toggle */}
      <div className="mb-6 pb-6 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <label htmlFor="faith-toggle" className="block text-sm font-medium text-neutral-700 mb-2">
              Enable Faith Track
            </label>
            <p className="text-sm text-neutral-600 mb-4">
              Add a fourth daily pillar with scripture-based devotionals and faith-focused reflection prompts. 
              This complements—not replaces—the core Mind, Move, and Journal activities.
            </p>
          </div>
          <div className="ml-4">
            <button
              id="faith-toggle"
              type="button"
              onClick={handleToggleFaithTrack}
              disabled={isLoading}
              className={`
                relative inline-flex h-8 w-14 items-center rounded-full transition-colors
                ${settings.faithTrackEnabled 
                  ? 'bg-green-600' 
                  : 'bg-neutral-300'
                }
                ${isLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
              `}
              aria-label="Toggle Faith Track"
            >
              <span
                className={`
                  inline-block h-6 w-6 transform rounded-full bg-white transition-transform
                  ${settings.faithTrackEnabled ? 'translate-x-7' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Faith Tradition Selector (only show if enabled) */}
      {settings.faithTrackEnabled && (
        <div className="mb-6">
          <label htmlFor="tradition-select" className="block text-sm font-medium text-neutral-700 mb-2">
            Faith Tradition
          </label>
          <p className="text-sm text-neutral-600 mb-3">
            Choose the faith tradition that aligns with your beliefs. Content will be tailored accordingly.
          </p>
          <select
            id="tradition-select"
            value={settings.faithTradition}
            onChange={handleTraditionChange}
            disabled={isLoading}
            className="
              w-full px-4 py-2 border border-neutral-300 rounded-lg 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              disabled:bg-neutral-50 disabled:cursor-not-allowed
              text-neutral-900
            "
          >
            {FAITH_TRADITION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-neutral-500 mt-2">
            Currently: <span className="font-medium">{selectedTraditionLabel}</span>
          </p>
        </div>
      )}

      {/* Feedback Messages */}
      {message.text && (
        <div
          className={`
            p-4 rounded-lg text-sm
            ${message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
            }
          `}
          role="alert"
        >
          {message.text}
        </div>
      )}

      {/* Information Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Privacy Note:</strong> Your faith entries are stored separately and remain private. 
          Only you and your assigned clinician (if any) can view them.
        </p>
      </div>
    </div>
  );
};

export default FaithSettings;
